import { FC, useState, useEffect, useMemo, useRef } from "react";
import { PiShoppingCartSimple } from "react-icons/pi";
import { SHOPIFY_STOREFRONT_GRAPHQL_API, SHOPIFY_STOREFRONT_TOKEN } from "../../../config";
import { ICartItemHeadless } from "../../../types";
import { useTrackContext } from "../../../hooks/useTrackContext";
import { GET_CART_LINES } from "../../../graphqlQueries";
import { RiCloseLine } from "react-icons/ri";
import { ROUTES } from "../../../consts";
import CartMobileView from "../subComponents/CartMobileView";
import { ImSpinner6 } from "react-icons/im";
import { Link } from "react-router-dom";
import RemoveItemConfirmationPopup from "../RemoveItemNotification/RemoveItemConfirmationPopup";

const MiniCart: FC = () => {
  const {
    headlessCartInfo,
    headlessCart,
    headlessCartTotalAmount,
    dispatch,
    trackSelectionOption,
    productRenderedLocation,
    isHeadlessMiniCartIsShowing,
    appHostedAt,
    mainTrackSelection,
    miniTrackSection,
    juniorTrackSelection,
    battleKartSelection,
    vrSelection,
  } = useTrackContext();

  const maxSelectableVariants = useMemo(() => {
    return trackSelectionOption.title === "MAIN_TRACK"
      ? mainTrackSelection.racesSeats.numberOfRaces
      : trackSelectionOption.title === "MINI_TRACK"
      ? miniTrackSection.racesSeats.numberOfRaces
      : trackSelectionOption.title === "JUNIOR_TRACK"
      ? juniorTrackSelection.racesSeats.numberOfRaces
      : trackSelectionOption.title === "BATTLE_KART_TRACK"
      ? battleKartSelection.racesSeats.numberOfRaces
      : vrSelection.playersandsessions.numberOfSessions;
  }, [
    trackSelectionOption.title,
    mainTrackSelection.racesSeats.numberOfRaces,
    miniTrackSection.racesSeats.numberOfRaces,
    juniorTrackSelection.racesSeats.numberOfRaces,
    battleKartSelection.racesSeats.numberOfRaces,
    vrSelection.playersandsessions.numberOfSessions,
  ]);

  const [headlessCartItems, setheadlessCartItems] = useState<ICartItemHeadless[]>([]);
  const isMiniCartShowing = useMemo(() => isHeadlessMiniCartIsShowing || false, [isHeadlessMiniCartIsShowing]);
  const [selectedTrackCartUrl, setSelectedTrackCartUrl] = useState<string>("");
  const [totalAmount, setTotalAmount] = useState<Number>(0);
  const [totalSaving, setTotalSaving] = useState<Number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const miniCartRef = useRef<HTMLDivElement | null>(null);

  const isCartEmptyTrue = useMemo(() => {
    return headlessCartItems.length === 0;
  }, [headlessCartItems]);

  useEffect(() => {
    const closeMiniCartOnOutsideClick = (event: MouseEvent) => {
      if (miniCartRef.current && !miniCartRef.current.contains(event.target as Node)) {
        dispatch({
          type: "SET_HEADLESS_IS_MINI_CART_SHOWING",
          payload: {
            isHeadlessMiniCartIsShowing: false,
          },
        });
      }
    };

    document.addEventListener("click", closeMiniCartOnOutsideClick);

    return () => {
      document.removeEventListener("click", closeMiniCartOnOutsideClick);
    };
  }, [dispatch]);

  useEffect(() => {
    setheadlessCartItems(headlessCart);
    setTotalAmount(Number(headlessCartTotalAmount));
  }, [headlessCart, headlessCartTotalAmount]);

  useEffect(() => {
    if (trackSelectionOption.title === "MAIN_TRACK") {
      setSelectedTrackCartUrl(ROUTES.INDEX + ROUTES.MAIN_TRACK.ReviewCart);
    } else if (trackSelectionOption.title === "MINI_TRACK") {
      setSelectedTrackCartUrl(ROUTES.INDEX + ROUTES.MINI_TRACK.ReviewCart);
    } else if (trackSelectionOption.title === "BATTLE_KART_TRACK") {
      setSelectedTrackCartUrl(ROUTES.INDEX + ROUTES.BATTLE_KART_TRACK.ReviewCart);
    } else {
      setSelectedTrackCartUrl(ROUTES.INDEX + ROUTES.VIRTUAL_REALITY.ReviewCart);
    }
  }, [trackSelectionOption.title]);

  useEffect(() => {
    setLoading(true);
    const graphqlEndpoint = SHOPIFY_STOREFRONT_GRAPHQL_API;
    const authToken = SHOPIFY_STOREFRONT_TOKEN;

    const headers = {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": authToken,
    };

    if (headlessCartInfo.id !== "") {
      const requestOptions = {
        method: "POST",
        headers,
        body: JSON.stringify({
          query: GET_CART_LINES,
          variables: {
            id: headlessCartInfo.id,
          },
        }),
      };

      fetch(graphqlEndpoint, requestOptions)
        .then(async (response) => {
          const responseData = await response.json();
          if (response.status === 200) {
            const cartData = responseData?.data?.cart;
            const subtotal = responseData?.data?.cart?.cost?.subtotalAmount?.amount;
            if (cartData) {
              const cartItems = cartData?.lines?.edges?.map((edge: { node: any }) => edge.node);
              setheadlessCartItems(cartItems);
              setTotalAmount(subtotal ? Number(subtotal) : 0);
              setTotalSaving(0);
            } else {
              console.error("Cart data is missing in the GraphQL response.", responseData);
            }
          } else {
            console.error("GraphQL error:", responseData?.errors);
          }
        })
        .catch((error) => {
          console.error("GraphQL request failed:", error);
        })
        .finally(() => setLoading(false));
    }
  }, [dispatch, headlessCartInfo]);

  const handleMiniCartShowOrHide = () =>
    dispatch({
      type: "SET_HEADLESS_IS_MINI_CART_SHOWING",
      payload: {
        isHeadlessMiniCartIsShowing: !isHeadlessMiniCartIsShowing,
      },
    });

  useEffect(() => {
    if (headlessCartItems.length === maxSelectableVariants && !isHeadlessMiniCartIsShowing) {
      dispatch({
        type: "SET_HEADLESS_IS_MINI_CART_SHOWING",
        payload: {
          isHeadlessMiniCartIsShowing: true,
        },
      });
    }
  }, [headlessCartItems.length, maxSelectableVariants, dispatch, isHeadlessMiniCartIsShowing]);

  return (
    <div className="tw-container tw-mx-auto tw-relative tw-z-[1000] tw-px-[10px] lg:tw-px-[0]" ref={miniCartRef}>
      {productRenderedLocation === "external" && <RemoveItemConfirmationPopup />}
      <div className={`tw-flex tw-justify-end tw-mr-[15px] xl:tw-mr-[0]`} style={appHostedAt && appHostedAt === "bosshunting" ? { marginRight: "15px" } : {}}>
        <div
          className={`tw-rounded-[100px] tw-p-[10px] tw-mt-[20px] tw-relative tw-cursor-pointer ${
            trackSelectionOption.title === "BATTLE_KART_TRACK" 
              ? "tw-bg-[linear-gradient(180deg,#FAC618_0%,#EA581A_100%)]" 
              : "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)]"
          }`}
          onClick={handleMiniCartShowOrHide}
        >
          <span className="tw-absolute tw-top-[0] tw-right-[0] tw-text-[white] tw-bg-[red] tw-rounded-full tw-text-[10px] tw-px-[4px]">
            {headlessCartItems.length >= 9 ? "9+" : headlessCartItems.length}
          </span>
          <PiShoppingCartSimple className="tw-w-[30px] tw-h-[30px] tw-text-[white]" />
        </div>
      </div>
      {isMiniCartShowing && (
        <div
          className={`tw-absolute tw-top-[5em] ${!appHostedAt && appHostedAt !== "bosshunting" ? "tw-right-[0px]" : ""} tw-px-[10px] lg:tw-px-[0]`}
          style={appHostedAt && appHostedAt === "bosshunting" ? { right: "15px" } : {}}
        >
          <div className={`${headlessCartItems.length !== 0 ? "tw-min-w-full md:tw-min-w-[500px]" : "tw-min-w-[300px]"} tw-w-fit tw-rounded-[8px] tw-overflow-x-hidden`}>
            {loading ? (
              <>
                <div className="tw-flex tw-justify-center tw-items-center tw-py-[10em] tw-mx-auto tw-w-full tw-bg-[#ffffff]">
                  <ImSpinner6 className="tw-flex tw-justify-center tw-text-[black] tw-mx-auto tw-h-[150px] tw-w-[150px] tw-animate-spin tw-transition tw-duration-300 tw-ease-in-out" />
                </div>
              </>
            ) : (
              <>
                <div className="tw-flex tw-justify-between tw-items-center tw-bg-[#EBECEF] tw-py-[10px] tw-px-[15px]">
                  <span className="tw-text-[16px] tw-text-[#393B41] tw-font-[700]">Your Cart ({headlessCartItems.length})</span>
                  <RiCloseLine className="tw-w-[30px] tw-h-[30px] tw-text-[#555758] tw-cursor-pointer" onClick={handleMiniCartShowOrHide} />
                </div>
                <div className="tw-bg-[#ffffff]">
                  {isCartEmptyTrue ? (
                    <>
                      <span className="tw-text-[black] tw-text-center tw-block tw-py-[10em] tw-font-[800]">Your Cart Is Empty</span>
                    </>
                  ) : (
                    <>
                      <div className="tw-max-h-[40vh] tw-h-full tw-overflow-y-auto">
                        <div className="tw-p-[20px]">
                          {headlessCartItems &&
                            !isCartEmptyTrue &&
                            headlessCartItems.length !== 0 &&
                            headlessCartItems.map((item) => {
                              return (
                                <CartMobileView
                                  headlessVariantID={item.id}
                                  key={item.merchandise.id}
                                  gameOptions={item.merchandise.selectedOptions as []}
                                  productType={item.merchandise.product.productType}
                                  isComponentUsedForMiniCart={true}
                                  productTittle={item.merchandise.product.title}
                                  variantTitle={item.merchandise.title}
                                  image={item.merchandise.image.url}
                                  title={item.merchandise.product.title}
                                  price={Number(item.merchandise.priceV2.amount)}
                                  quantity={item.quantity}
                                  id={item.merchandise.id}
                                  compareAtPrice={item.merchandise.compareAtPrice ? Number(item.merchandise.compareAtPrice.amount) : 0}
                                />
                              );
                            })}
                        </div>
                      </div>
                      <div className="tw-p-[20px]">
                        <div className="tw-border-[1.5px] tw-border-[#EBECEF] tw-mb-[15px]" />
                        <div className="tw-flex tw-justify-between">
                          <span className="tw-text-[#393B41] tw-text-[14px] tw-font-[600]">Subtotal</span>
                          <span className="tw-text-[#393B41] tw-text-[16px] tw-font-[700]">${Number(totalAmount)}</span>
                        </div>
                        <div className="tw-flex tw-justify-between tw-my-[15px]">
                          <span className="tw-text-[#393B41] tw-text-[14px] tw-font-[600]">Total Savings</span>
                          <span className="tw-text-[#393B41] tw-text-[16px] tw-font-[700]">${Number(totalSaving)}</span>
                        </div>

                        <span
                          onClick={() => (window.location.href = headlessCartInfo.checkoutUrl)}
                          className={`tw-flex tw-items-center tw-justify-center tw-text-center tw-text-white tw-py-[16px] tw-px-[40px] tw-rounded-full tw-cursor-pointer tw-w-full tw-text-[18px] tw-font-[700] ${
                            trackSelectionOption.title === "BATTLE_KART_TRACK" 
                              ? "tw-bg-[linear-gradient(180deg,#FAC618_0%,#EA581A_100%)]" 
                              : "bg-gradient-2"
                          }`}
                        >
                          Proceed to Checkout
                        </span>

                        <Link
                          to={selectedTrackCartUrl}
                          className={`tw-mt-[15px] tw-flex tw-items-center tw-justify-center tw-text-center tw-py-[16px] tw-px-[40px] tw-border-[1px] tw-rounded-full tw-cursor-pointer tw-w-full tw-text-[18px] tw-font-[700] ${
                            trackSelectionOption.title === "BATTLE_KART_TRACK" 
                              ? "tw-text-[#EA581A] tw-border-[#EA581A]" 
                              : "tw-text-[#9D62FE] tw-border-[#9D62FE]"
                          }`}
                        >
                          Go to Cart
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MiniCart;
